/**
 * ResponsibilityPicker — Bereich (PU/Service/MTB) manuell setzen.
 * Quelle: §10.18-Followup — User wollte den Bereich-Tag in Übergaben-Sicht
 * und Project-Detail überschreiben können.
 *
 * V1.0: Bereich wird beim Sync konkret aus SF gesetzt (am führenden Auftrag).
 * Dieser Picker setzt den **Override** am Projekt (projects.responsibility) und
 * gewinnt gegenüber SF. Die frühere "Auto (aus SF)"-Option entfällt — ohne
 * Override zeigt die Karte den SF-Wert bzw. "offen" (siehe BereichBadge).
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api.js';
import { useToast } from '../toast.js';

type Responsibility = 'PU' | 'Service' | 'MTB';
const OPTIONS: Responsibility[] = ['PU', 'Service', 'MTB'];

export function ResponsibilityPicker({
  projectId,
  value,
  canEdit,
  onSaved,
}: {
  projectId: string;
  value: Responsibility | null | undefined;
  canEdit: boolean;
  onSaved?: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();

  const mut = useMutation({
    mutationFn: (next: Responsibility | null) =>
      api.patchProject(projectId, { responsibility: next }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['v2-project', projectId] });
      void queryClient.invalidateQueries({ queryKey: ['v2-mc-projects'] });
      void queryClient.invalidateQueries({ queryKey: ['v2-board-projects'] });
      void queryClient.invalidateQueries({ queryKey: ['v2-atlas-projects', 'all'] });
      void queryClient.invalidateQueries({ queryKey: ['v2-handover-projects'] });
      toast.success(t('v2.responsibility.saved', 'Bereich gespeichert'));
      onSaved?.();
    },
    onError: (err) =>
      toast.error(
        t('v2.responsibility.failed', 'Speichern fehlgeschlagen'),
        err instanceof Error ? err.message : 'Fehler',
      ),
  });

  return (
    <select
      value={value ?? ''}
      onChange={(e) => {
        const v = e.target.value as Responsibility | '';
        if (v === '') return; // Placeholder nicht wählbar
        mut.mutate(v);
      }}
      disabled={!canEdit || mut.isPending}
      title={
        !canEdit
          ? t('v2.reader.tooltip', 'Sie haben Leserechte')
          : t('v2.responsibility.tooltip', 'Bereich überschreibt SF-Vorgabe')
      }
      className="text-xs px-2 py-1 rounded outline-none"
      style={{
        background: 'var(--surface-2)',
        color: 'var(--ink)',
        border: '1px solid var(--line-soft)',
        fontFamily: 'var(--f-mono)',
        cursor: canEdit ? 'pointer' : 'not-allowed',
        opacity: canEdit && !mut.isPending ? 1 : 0.6,
      }}
    >
      <option value="" disabled>
        {t('v2.responsibility.placeholder', '— Bereich wählen —')}
      </option>
      {OPTIONS.map((o) => (
        <option key={o} value={o}>
          {o}
        </option>
      ))}
    </select>
  );
}
